import React, { useCallback, useEffect, useState } from "react";
import {
  searchData,
  expandSearchedValue,
  checkPhone,
  handlePhoneAction,
  handleCreatePhone,
} from "../utilities/webRTCHelpers";
import "../App.css";
import {
  AlertBlock,
  DataTable,
  DxButton,
  DxTextbox,
  LoadingPlaceholder,
} from "genesys-react-components";
import { GenesysDevIcon, GenesysDevIcons } from "genesys-dev-icons";

const WebRTCFix = ({ authenticate }) => {
  const [state, setState] = useState({
    valueData: [],
    value: "",
    loading: false,
    error: null,
    loadingReassociateUserId: null,
    reassociateUserId: null,
    loadingRebootUserId: null,
    rebootUserId: null,
  });

  const {
    valueData,
    value,
    loading,
    error,
    loadingReassociateUserId,
    reassociateUserId,
    loadingRebootUserId,
    rebootUserId,
  } = state;

  const memoizedExpandSearchedValue = useCallback(
    (id) => expandSearchedValue(id, valueData),
    [valueData]
  );

  const memoizedCheckPhone = useCallback(
    (id) => checkPhone(id, valueData),
    [valueData]
  );

  useEffect(() => {
    if (!valueData) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: "No data found.",
      }));
    } else if (valueData.length > 0) {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      Promise.all(valueData.map((user) => memoizedExpandSearchedValue(user.id)))
        .then(() => valueData.map((user) => memoizedCheckPhone(user.id)))
        .then(() => {
          setTimeout(
            () => setState((prev) => ({ ...prev, loading: false })),
            1000
          );
        });
    }
  }, [valueData, memoizedExpandSearchedValue, memoizedCheckPhone]);

  useEffect(() => {
    setState((prev) => ({ ...prev, error: null, valueData: [] }));
  }, [authenticate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setState((prev) => ({ ...prev, valueData: [] }));
    searchData(value, setState);
  };

  const userValue = valueData
    ? valueData.map((user) => {
        let stations = [];
        if (!user.station && !user.webRtcPhone) {
          stations.push(
            <p key="noStation">
              <GenesysDevIcon
                className="warning-icon"
                icon={GenesysDevIcons.AppWarnSolid}
              />{" "}
              No station
            </p>
          );
        } else {
          const stationTypes = [
            { key: "effectiveStation", label: "Phone assigned" },
            { key: "associatedStation", label: "User logged in" },
            { key: "defaultStation", label: "Default phone assigned" },
          ];

          stationTypes.forEach(({ key, label }) => {
            stations.push(
              <p key={key}>
                <GenesysDevIcon
                  className={
                    user.station && user.station[key]
                      ? "success-icon"
                      : "critical-icon"
                  }
                  icon={
                    user.station && user.station[key]
                      ? GenesysDevIcons.AppCheck
                      : GenesysDevIcons.AppTimes
                  }
                />{" "}
                {label}
              </p>
            );
          });
        }
        return {
          cells: [
            {
              content: user.name,
              align: "left",
            },
            {
              content: user.email,
              align: "left",
            },
            {
              renderedContent: (
                <>
                  {loadingRebootUserId === user.id && (
                    <span className="loading loading-spinner"></span>
                  )}
                  {rebootUserId === user.id && (
                    <AlertBlock
                      title="Reboot successful"
                      alertType="success"
                      collapsible={false}
                      autoCollapse={false}
                      indentation={1}
                      className="clazzy-alertblock snackbar-animation"
                    />
                  )}
                  {!user.webRtcPhone ? (
                    <DxButton
                      type="link"
                      onClick={() =>
                        handleCreatePhone(user.id, user.name, authenticate, setState)
                      }
                      disabled={false}
                      className="tool-button"
                    >
                      <GenesysDevIcon icon={GenesysDevIcons.AppPlusStroke} />
                    </DxButton>
                  ) : (
                    <>
                      {loadingRebootUserId !== user.id &&
                        rebootUserId !== user.id && (
                          <>
                            <span>{user.webRtcPhone.name}</span>
                            <DxButton
                              type="link"
                              onClick={() =>
                                handlePhoneAction(
                                  user.id,
                                  "rebootPhone",
                                  setState,
                                  memoizedExpandSearchedValue,
                                  memoizedCheckPhone
                                )
                              }
                              disabled={false}
                              className="tool-button"
                            >
                              <GenesysDevIcon
                                icon={GenesysDevIcons.AppRefresh}
                              />
                            </DxButton>
                          </>
                        )}
                    </>
                  )}
                </>
              ),
              content: !user.webRtcPhone ? "No phone" : user.webRtcPhone.name,
              align: "left",
            },
            {
              renderedContent: (
                <>
                  {loadingReassociateUserId === user.id && (
                    <span className="loading loading-spinner"></span>
                  )}
                  {reassociateUserId === user.id && (
                    <AlertBlock
                      title="Refresh successful"
                      alertType="success"
                      collapsible={false}
                      autoCollapse={false}
                      indentation={1}
                      className="clazzy-alertblock snackbar-animation"
                    />
                  )}
                  {stations.length > 1 ? (
                    loadingReassociateUserId !== user.id &&
                    reassociateUserId !== user.id && (
                      <>
                        <span>{stations}</span>
                        <DxButton
                          type="link"
                          onClick={() =>
                            handlePhoneAction(
                              user.id,
                              "reassociateStation",
                              setState,
                              memoizedExpandSearchedValue,
                              memoizedCheckPhone
                            )
                          }
                          disabled={false}
                          className="tool-button"
                        >
                          <GenesysDevIcon icon={GenesysDevIcons.DestWrench} />
                        </DxButton>
                      </>
                    )
                  ) : (
                    <span>{stations}</span>
                  )}
                </>
              ),
              content: "Status",
              align: "left",
            },
          ],
        };
      })
    : [];

  const tableProps = {
    className: "table",
    headerRow: {
      cells: [
        {
          content: "User",
          align: "left",
        },
        {
          content: "Email",
          align: "left",
        },
        {
          content: "Phone",
          align: "left",
        },
        {
          content: "Status",
          align: "left",
        },
      ],
    },
    indentation: 0,
    rows: userValue,
  };

  return (
    <>
      {loading ? (
        <LoadingPlaceholder text="Fetching data" />
      ) : (
        <>
          <form onSubmit={handleSubmit}>
            <DxTextbox
              inputType="text"
              label="Search: "
              placeholder="Enter name or email"
              icon={GenesysDevIcons.AppSearch}
              clearButton={true}
              onChange={(value) => setState((prev) => ({ ...prev, value }))}
              className="user-input"
              autoComplete="off"
              changeDebounceMs={1}
            />

            <button type="submit" className="button-submit">
              Submit
            </button>
          </form>
          {error ? (
            <AlertBlock
              title={error}
              alertType="critical"
              className="clazzy-alertblock error-search"
            />
          ) : (
            <DataTable
              headerRow={tableProps.headerRow}
              rows={tableProps.rows}
            />
          )}
        </>
      )}
    </>
  );
};

export default WebRTCFix;
